/**
 * Image Component
 *
 * A reusable image component with support for Tailwind CSS styling.
 * Offers features like lazy loading, event handling, and flexible design
 * customization using utility classes.
 *
 * @author Team CMS
 */
'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { cn } from '../../../utils/index.js';
import { generateSrcsetString } from '../../core/utils/generateSrcsetString.js';
import { getDefaultImgUrl } from '../../core/utils/getDefaultImgUrl.js';
/**
 * A functional React component for rendering images.
 *
 * @param props - Props for configuring the Image component.
 * @param props.src
 * @param props.alt
 * @param props.width
 * @param props.height
 * @param props.className
 * @param props.shouldFitWrapper
 * @param props.loading
 * @param props.onLoad
 * @param props.onError
 * @param props.decoding
 * @param props.title
 * @param props.sizes
 * @param props.requestedWidths
 * @param props.backgroundColor
 * @returns A JSX element representing the image.
 */
export default function Image({ src, alt, width, height, className = '', shouldFitWrapper = false, loading = 'eager', onLoad, onError, decoding = 'auto', title, sizes = '100vw', requestedWidths, backgroundColor = '', }) {
    // Resolve the default fallback URL to the closest matching generated width.
    const resolvedUrl = useMemo(() => getDefaultImgUrl(src, requestedWidths, width), [src, requestedWidths, width]);
    // Generate the srcSet string for responsive images.
    const srcSetString = useMemo(() => generateSrcsetString(src, requestedWidths, width), [src, requestedWidths, width]);
    return (_jsxs("div", { className: cn('relative block h-full w-full', className), style: { backgroundColor }, itemScope: true, itemType: "https://schema.org/ImageObject", children: [_jsx("meta", { content: resolvedUrl, itemProp: "url" }), _jsx("img", { src: resolvedUrl, alt: alt || 'Image', width: width, height: height, loading: loading, className: cn('w-full', shouldFitWrapper && 'h-full object-cover'), onLoad: onLoad, onError: onError, decoding: decoding, title: title, srcSet: srcSetString, sizes: sizes, itemProp: "image" })] }));
}
//# sourceMappingURL=Image.js.map